import { useEffect } from "react";
import { supabase } from "@/lib/supabase";

const REFRESH_WINDOW_SECONDS = 5 * 60; // 5 minutes
const CHECK_INTERVAL_MS = 60_000; // 1 minute

let refreshing: Promise<void> | null = null;

async function ensureFreshSession() {
  try {
    const { data } = await supabase.auth.getSession();
    const session = data.session;
    if (!session?.expires_at) return;
    const secondsLeft = session.expires_at - Math.floor(Date.now() / 1000);
    if (secondsLeft > REFRESH_WINDOW_SECONDS) return;
    if (refreshing) return refreshing;
    refreshing = supabase.auth
      .refreshSession()
      .then(() => undefined)
      .catch(() => undefined)
      .finally(() => {
        refreshing = null;
      });
    await refreshing;
  } catch {
    // Best-effort — never throw from the guard.
  }
}

/**
 * Lightweight session guard. Polls every minute and proactively refreshes
 * the Supabase session when it is within 5 minutes of expiry, so subsequent
 * queries don't fire with a stale or expired token.
 */
export function useSessionGuard() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    void ensureFreshSession();
    const id = window.setInterval(() => void ensureFreshSession(), CHECK_INTERVAL_MS);
    const onFocus = () => void ensureFreshSession();
    window.addEventListener("focus", onFocus);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, []);
}
